/**
 * A situation typed in words becomes a runnable thread on its own: the scenario type it tests,
 * the world the sidebar shows, the customer's opening turns. Three AI calls in a row, each result
 * landing where the simulator already reads it, paced so the screen fills one piece at a time.
 */

import { atom, type Setter } from 'jotai';
import { resolvedBusinessUnitIdAtom } from '@/hooks/use-business-units';
import { generateMessageId } from '@/hooks/use-chat';
import { aiFunctionService } from '@/services/ai-function.service';
import { chatMessagesAtom } from '@/stores/chat.store';
import { filledSections, type SimulationConfig } from '../session/scenario-case';
import { channelIdAtom, describedScenarioAtom } from '../session/simulator-session.store';
import {
  highlightedSectionsAtom,
  openSectionsAtom,
  simulationProfileAtom,
  type SimulationProfileExtras,
} from '../sidebar/simulation-profile.store';

type DescribeStep = 'type' | 'world' | 'thread';
type DescribeStatus = 'idle' | 'editing' | DescribeStep | 'error';

interface DescribeFlow {
  status: DescribeStatus;
  situation: string;
  error: string | null;
}

export const IDLE_DESCRIBE_FLOW: DescribeFlow = { status: 'idle', situation: '', error: null };
export const describeFlowAtom = atom<DescribeFlow>(IDLE_DESCRIBE_FLOW);

/** The pace the screen fills at: a sidebar section, then a turn, each its own beat. */
const SECTION_BEAT_MS = 450;
const TURN_BEAT_MS = 800;
const HIGHLIGHT_MS = 1800;
const PAUSE_MS = 400;
const SECTION_ORDER = ['customer', 'order', 'subscription', 'product'];

const FAILURES: Record<DescribeStep, string> = {
  type: 'Could not read the situation. Try again.',
  world: 'Could not set up the world. Try again.',
  thread: 'Could not write the conversation. Try again.',
};

// One flow at a time: a reset or a new start drops whatever the previous one still had scheduled.
let generation = 0;
const pending = new Set<ReturnType<typeof setTimeout>>();

const later = (ms: number, work: () => void) => {
  const timer = setTimeout(() => {
    pending.delete(timer);
    work();
  }, ms);
  pending.add(timer);
};

const begin = (): number => {
  for (const timer of pending) clearTimeout(timer);
  pending.clear();
  return ++generation;
};

const sectionOf = (key: string): string => key.split('_')[0];

const sectionFields = (world: SimulationConfig, section: string): SimulationConfig =>
  Object.fromEntries(Object.entries(world).filter(([key]) => sectionOf(key) === section)) as SimulationConfig;

/** What the tester typed into the sidebar before describing; the world fills around it, never over it. */
const typedFields = (profile: SimulationProfileExtras): SimulationConfig =>
  Object.fromEntries(
    Object.entries(profile).filter(([, value]) => (Array.isArray(value) ? value.length > 0 : String(value ?? '').trim() !== ''))
  ) as SimulationConfig;

/** One section lands: its fields, its row opened, and a highlight that fades on its own. */
const reveal = (set: Setter, section: string, fields: SimulationConfig) => {
  set(simulationProfileAtom, (prev) => ({ ...prev, ...fields }));
  set(openSectionsAtom, (prev) => (prev.includes(section) ? prev : [...prev, section]));
  set(highlightedSectionsAtom, (prev) => [...prev, section]);
  later(HIGHLIGHT_MS, () => set(highlightedSectionsAtom, (prev) => prev.filter((id) => id !== section)));
};

export const cancelDescribeFlowAtom = atom(null, (_get, set) => {
  begin();
  set(describeFlowAtom, IDLE_DESCRIBE_FLOW);
  set(highlightedSectionsAtom, []);
});

export const startDescribeFlowAtom = atom(null, async (get, set, situation: string) => {
  const gen = begin();
  const live = () => gen === generation;
  let step: DescribeStep = 'type';
  const advance = (next: DescribeStep) => {
    step = next;
    set(describeFlowAtom, { status: next, situation, error: null });
  };

  advance('type');
  set(describedScenarioAtom, { situation, scenarioType: null });
  set(highlightedSectionsAtom, []);
  try {
    const scenarioType = await aiFunctionService.describeScenarioType({ situation }, get(resolvedBusinessUnitIdAtom));
    if (!live()) return;
    if (!scenarioType) throw new Error('no scenario type');
    set(describedScenarioAtom, { situation, scenarioType });

    advance('world');
    const typed = typedFields(get(simulationProfileAtom));
    const toolOutput = await aiFunctionService.describeScenarioWorld({
      situation,
      scenarioType,
      ...(Object.keys(typed).length ? { known: typed } : {}),
    });
    if (!live()) return;
    const world: SimulationConfig = { ...toolOutput, ...typed };
    const sections = SECTION_ORDER.filter((section) => filledSections(toolOutput).has(section));
    sections.forEach((section, i) => later(i * SECTION_BEAT_MS, () => reveal(set, section, sectionFields(world, section))));
    const worldShownAt = Date.now() + sections.length * SECTION_BEAT_MS;

    advance('thread');
    const turns = await aiFunctionService.describeScenarioThread({ situation, scenarioType, toolOutput: world });
    if (!live()) return;
    if (!turns.length) throw new Error('no turns');
    // The turns wait for the last section to land, then arrive one by one.
    const startIn = Math.max(0, worldShownAt - Date.now()) + PAUSE_MS;
    turns.forEach((turn, i) =>
      later(startIn + i * TURN_BEAT_MS, () => {
        const channelId = get(channelIdAtom);
        set(chatMessagesAtom, [
          ...get(chatMessagesAtom),
          {
            id: generateMessageId(),
            from: turn.role === 'customer' ? 'user' : 'assistant',
            content: turn.text,
            timestamp: Date.now(),
            ...(turn.role === 'customer' && channelId ? { channelId } : {}),
          },
        ]);
      })
    );
    later(startIn + turns.length * TURN_BEAT_MS, () => set(describeFlowAtom, IDLE_DESCRIBE_FLOW));
  } catch {
    if (live()) set(describeFlowAtom, { status: 'error', situation, error: FAILURES[step] });
  }
});
